import React, { FC, KeyboardEvent, useMemo, useRef, useState } from 'react';
import {
  ComponentRendering,
  Field,
  Image,
  ImageField,
  LinkField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { FileField } from '@sitecore-jss/sitecore-jss-react/types/components/File';
import classNames from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';
import { sanitize } from 'isomorphic-dompurify';

import { useBreakpoint } from '@gn-audio/jss-components';
import { Button, getContrastThemeClass, resolveColorProperty } from '@gn-audio/jss-components';

import { JabraSlider, JabraSliderRef } from 'common/components/JabraSlider/JabraSlider';
import { PillsSliderNavigation } from 'common/components/JabraSlider/PillsSliderNavigation';
import { useLazyVideo } from 'common/hooks/useLazyVideo';
import { usePlaceholderContainer } from 'components/Product/KSP/Placeholder/PlaceholderContainerContext';
import VideoEmbed from 'composables/Product/VideoEmbed/VideoEmbed';

export type ContentSliderSpotFields = {
  background: Field<string>;
  description: Field<string>;
  headline: Field<string>;
  slides: ContentSliderItem[];
};

export type ContentSliderSpotProps = {
  fields: ContentSliderSpotFields;
  rendering: ComponentRendering;
};

export type ContentSliderItem = {
  description: TextField;
  headline: TextField;
  id: string;
  image: ImageField;
  link: LinkField;
  linkTitle: TextField;
  linkAriaLabel?: Field<string>;
  size: Field<'full' | 'half' | 'quarter'>;
  video: FileField;
  youkuVideoId: TextField;
  youtubeVideoId: TextField;
  insideSlide: Field<boolean>;
  textBackground: Field<string>;
};

const sizeToClass = {
  full: 'flex-[0_0_100%]',
  half: 'flex-[0_0_100%] md:flex-[0_0_50%]',
  quarter: 'flex-[0_0_100%] md:flex-[0_0_25%]',
};

const SlideItem: React.FC<{ item: ContentSliderItem }> = ({ item }) => {
  const {
    youtubeVideoId,
    youkuVideoId,
    video,
    image,
    headline,
    description,
    link,
    linkTitle,
    linkAriaLabel,
    size,
    insideSlide,
    textBackground,
  } = item;

  const hasVideoSrc = youtubeVideoId?.value || youkuVideoId?.value;
  const hasRegularVideo = Boolean(video?.value);

  const { videoRef, containerRef } = useLazyVideo();

  const { isAboveLg } = useBreakpoint('lg', true);

  const shouldOverlayText =
    insideSlide?.value && !youtubeVideoId?.value && size?.value === 'full' && isAboveLg;

  const textOverlayStyles = useMemo(() => {
    const bgColor = resolveColorProperty(textBackground?.value as string) ?? '#ffffff';
    const themeClass = getContrastThemeClass(textBackground?.value as string);

    return {
      bgColor,
      themeClass,
      style: shouldOverlayText ? { backgroundColor: `${bgColor}E6` } : undefined,
      classes: shouldOverlayText
        ? `absolute left-16 top-16 z-10 w-1/3 rounded-xl py-8 px-10 drop-shadow backdrop-blur-md ${themeClass}`
        : 'mt-6 px-3',
    };
  }, [shouldOverlayText, textBackground?.value]);

  return (
    <div className="relative w-full">
      <figure ref={hasRegularVideo ? containerRef : undefined} className="w-full">
        {hasVideoSrc ? (
          <VideoEmbed
            youtubeId={youtubeVideoId?.value as string}
            youkuId={youkuVideoId?.value as string}
            className="aspect-video w-full rounded-lg"
          />
        ) : video?.value ? (
          <video
            data-src={video.value.src}
            ref={videoRef}
            autoPlay
            loop
            playsInline
            muted
            className="lazy w-full max-w-full rounded-lg"
            preload="none"
          />
        ) : image?.value?.src ? (
          <Image
            className="h-full w-full max-w-full rounded-lg object-cover"
            field={image.value}
            loading="lazy"
          />
        ) : null}
      </figure>
      <div className={classNames(textOverlayStyles.classes)} style={textOverlayStyles.style}>
        <p
          className="heading-5 font-bold"
          dangerouslySetInnerHTML={{ __html: sanitize(headline.value as string) }}
        />
        <p
          className="paragraph"
          dangerouslySetInnerHTML={{ __html: sanitize(description.value as string) }}
        />
        {link?.value?.href && linkTitle?.value && (
          <Button
            variant="tertiary"
            asLink
            size="m"
            href={link.value.href}
            className="mt-4 flex"
            {...(linkAriaLabel?.value && { 'aria-label': linkAriaLabel.value })}
          >
            {linkTitle.value}
          </Button>
        )}
      </div>
    </div>
  );
};

const shouldEnableSlider = (slides: ContentSliderItem[]) => {
  const { fullCount, halfCount, quarterCount } = slides.reduce(
    (acc, slide) => {
      if (slide.size.value === 'full') acc.fullCount++;
      if (slide.size.value === 'half') acc.halfCount++;
      if (slide.size.value === 'quarter') acc.quarterCount++;
      return acc;
    },
    { fullCount: 0, halfCount: 0, quarterCount: 0 }
  );

  return (
    fullCount >= 1 || halfCount >= 2 || quarterCount >= 4 || (quarterCount >= 2 && halfCount >= 1)
  );
};

const ContentSliderSpot: FC = ({ fields: unparsedFields }: { fields: ContentSliderSpotFields }) => {
  const fields = unparsedFields;
  const { background, description, headline, slides } = fields;
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [startIndex] = useState<number>(0);
  const jabraSlider = useRef<JabraSliderRef>(null);
  const { isInsidePlaceholder } = usePlaceholderContainer();

  const backgroundColorHex = resolveColorProperty(background?.value?.toLowerCase());

  const containerClass = classNames({
    'bg-transparent': isInsidePlaceholder && !backgroundColorHex,
  });

  const getLightOrDarkTheme = backgroundColorHex ? getContrastThemeClass(backgroundColorHex) : null;

  if (!slides || slides.length === 0) {
    return <div className="container-fluid">No slides found</div>;
  }
  const enableSlider = shouldEnableSlider(slides);

  const sliderSettings: EmblaOptionsType = {
    containScroll: false,
    active: enableSlider,
    align: 'start',
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (jabraSlider.current) {
      if (event.key === 'ArrowLeft') {
        jabraSlider.current.prev();
      } else if (event.key === 'ArrowRight') {
        jabraSlider.current.next();
      }
    }
  };

  const handleMoveToSlide = (index: number) => {
    jabraSlider.current?.goTo(index);
  };

  return (
    <div
      style={backgroundColorHex && { backgroundColor: backgroundColorHex }}
      onKeyDown={handleKeyDown}
      className={classNames('container-fluid', containerClass, getLightOrDarkTheme)}
    >
      <div className="m-auto mb-12 flex max-w-[1440px] flex-col justify-between gap-8 px-3 md:flex-row md:items-center 2xl:px-0">
        <div>
          <div className="heading-2 mb-0">{headline.value}</div>
          {description && <p className="paragraph mt-4">{description.value}</p>}
        </div>
        {enableSlider && (
          <PillsSliderNavigation
            activeIndex={activeIndex}
            total={fields.slides.length}
            buttonVariant="primary"
            sliderButtonPosition="start"
            goTo={handleMoveToSlide}
            className="self-end"
          />
        )}
      </div>
      <JabraSlider
        mainClassName="relative w-full focus:outline-none max-w-[1440px]"
        className="overflow-hidden"
        startIndex={startIndex}
        overflow
        sliderSettings={sliderSettings}
        ref={jabraSlider}
        onChange={setActiveIndex}
        showSlideArrows={false}
      >
        {slides.map((item) => (
          <SlideItem
            key={item.id}
            item={item}
            data-single-slide-classname={classNames('px-3', sizeToClass[item.size.value])}
          />
        ))}
      </JabraSlider>
    </div>
  );
};

export default withDatasourceCheck()<ContentSliderSpotProps>(ContentSliderSpot);
